import { AnimatePresence, motion } from "framer-motion";
import { AtSign, CheckCircle2, Loader2, Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";

import { BachecaThreadView } from "@/components/BachecaThreadView";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  useAggiornaBacheca,
  useChiudiBacheca,
  useMarcaBachecaLetto,
} from "@/hooks/useAdminBacheca";
import { useAdminUtenti } from "@/hooks/useAdminUtenti";
import { useModuli } from "@/hooks/useModuli";
import { cn } from "@/lib/utils";
import type {
  BachecaTipo,
  BachecaVoce,
  ParteResponsabile,
} from "@/types/api";

const TIPI: { value: BachecaTipo; label: string }[] = [
  { value: "nota", label: "Nota" },
  { value: "decisione", label: "Decisione" },
  { value: "blocco", label: "Blocco" },
];

const PARTI: { value: ParteResponsabile; label: string }[] = [
  { value: "misterpet", label: "Mister Pet" },
  { value: "maitime", label: "Maitime" },
  { value: "entrambi", label: "Entrambi" },
];

interface BachecaSidePanelProps {
  voce: BachecaVoce | null;
  parteUtente: ParteResponsabile;
  /** "admin" abilita modifica + chiusura. "cliente" è read-only del thread. */
  mode: "admin" | "cliente";
  onClose: () => void;
}

export function BachecaSidePanel({
  voce,
  parteUtente,
  mode,
  onClose,
}: BachecaSidePanelProps) {
  const open = voce !== null;
  // Esc chiude il panel
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && voce && (
        <>
          {/* Overlay leggero solo su mobile, click chiude. Desktop: nessun overlay. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-foreground/20 z-30 md:hidden"
            onClick={onClose}
            aria-hidden
          />

          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 30 }}
            className={cn(
              "fixed inset-y-0 right-0 z-40 w-full md:w-[480px]",
              "bg-background border-l shadow-2xl flex flex-col"
            )}
            role="dialog"
            aria-modal="false"
            aria-label="Dettaglio voce di bacheca"
          >
            <BachecaSidePanelContent
              voce={voce}
              parteUtente={parteUtente}
              mode={mode}
              onClose={onClose}
            />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

// ── Contenuto del panel ──────────────────────────────────────────────

function BachecaSidePanelContent({
  voce,
  parteUtente,
  mode,
  onClose,
}: BachecaSidePanelProps & { voce: BachecaVoce }) {
  const [editMode, setEditMode] = useState(false);
  const [chiudiMode, setChiudiMode] = useState(false);
  const marcaLetto = useMarcaBachecaLetto();

  // Marca come letto la prima volta che apriamo il panel su voce con
  // messaggi non letti. Solo per admin.
  useEffect(() => {
    if (mode === "admin" && voce.ha_messaggi_non_letti) {
      marcaLetto.mutate(voce.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voce.id, mode]);

  // Reset modalità interna quando cambia voce
  useEffect(() => {
    setEditMode(false);
    setChiudiMode(false);
  }, [voce.id]);

  const isChiusa = voce.stato === "chiuso";
  const tipoLabel = TIPI.find((t) => t.value === voce.tipo)?.label ?? voce.tipo;

  return (
    <>
      {/* Header */}
      <header className="border-b px-5 py-4 flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px]">
              {tipoLabel}
            </Badge>
            {isChiusa && (
              <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">
                chiusa
              </Badge>
            )}
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              verso{" "}
              {PARTI.find((p) => p.value === voce.parte_responsabile)?.label ??
                voce.parte_responsabile}
            </span>
          </div>
          <h2 className="text-base font-semibold leading-snug pr-2">
            {voce.titolo}
          </h2>
          {voce.assegnato_a_nome && (
            <div className="flex items-center gap-1.5 text-xs pt-0.5">
              <AtSign className="size-3 text-brand-700" />
              <span className="text-muted-foreground">Rivolta a</span>
              <span className="font-medium text-foreground">
                {voce.assegnato_a_nome}
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground -mr-1 p-1 rounded-md hover:bg-muted/50 shrink-0"
          aria-label="Chiudi pannello"
        >
          <X className="size-4" />
        </button>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {editMode ? (
          <EditForm
            voce={voce}
            onCancel={() => setEditMode(false)}
            onSaved={() => setEditMode(false)}
          />
        ) : chiudiMode ? (
          <ChiudiForm
            voce={voce}
            onCancel={() => setChiudiMode(false)}
            onClosed={() => {
              setChiudiMode(false);
              onClose();
            }}
          />
        ) : (
          <BachecaThreadView voce={voce} parteUtente={parteUtente} />
        )}
      </div>

      {/* Footer azioni (solo admin, voce aperta, modalità default) */}
      {mode === "admin" && !isChiusa && !editMode && !chiudiMode && (
        <footer className="border-t px-5 py-3 flex items-center justify-end gap-2 bg-muted/20">
          <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
            <Pencil className="size-3.5" />
            Modifica voce
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setChiudiMode(true)}>
            <CheckCircle2 className="size-3.5" />
            Chiudi voce
          </Button>
        </footer>
      )}

      {mode === "admin" && isChiusa && voce.motivo_chiusura && (
        <footer className="border-t px-5 py-3 bg-muted/20">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Motivo chiusura
          </p>
          <p className="text-sm">{voce.motivo_chiusura}</p>
        </footer>
      )}
    </>
  );
}

// ── Form modifica ────────────────────────────────────────────────────

function EditForm({
  voce,
  onCancel,
  onSaved,
}: {
  voce: BachecaVoce;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [titolo, setTitolo] = useState(voce.titolo);
  const [corpo, setCorpo] = useState(voce.corpo ?? "");
  const [tipo, setTipo] = useState<BachecaTipo>(voce.tipo);
  const [parte, setParte] = useState<ParteResponsabile>(voce.parte_responsabile);
  const [moduloId, setModuloId] = useState<string | null>(voce.modulo_id);
  const [visCliente, setVisCliente] = useState(voce.is_visibile_cliente);
  const [assegnatoId, setAssegnatoId] = useState<string | null>(
    voce.assegnato_a_id
  );
  const { data: moduli = [] } = useModuli();
  const { data: utenti = [] } = useAdminUtenti();
  const mutation = useAggiornaBacheca();

  // Destinatari candidati: utenti MP attivi che hanno fatto login almeno una
  // volta (hanno `utente_id`). Senza login non c'è id da assegnare.
  const destinatariCandidati = utenti
    .filter((u) => u.attivo && u.parte === "misterpet" && u.utente_id)
    .sort((a, b) =>
      (a.nome_completo ?? a.email).localeCompare(b.nome_completo ?? b.email)
    );

  function salva() {
    if (!titolo.trim()) return;
    mutation.mutate(
      {
        voceId: voce.id,
        body: {
          titolo: titolo.trim(),
          corpo: corpo.trim() || null,
          tipo,
          parte_responsabile: parte,
          modulo_id: moduloId,
          is_visibile_cliente: visCliente,
          assegnato_a_id: assegnatoId,
        },
      },
      { onSuccess: onSaved }
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">
        Modifica voce
      </p>
      <FormField label="Titolo">
        <input
          value={titolo}
          onChange={(e) => setTitolo(e.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        />
      </FormField>
      <FormField label="Corpo">
        <Textarea value={corpo} onChange={(e) => setCorpo(e.target.value)} rows={4} />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Tipo">
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as BachecaTipo)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {TIPI.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Verso">
          <select
            value={parte}
            onChange={(e) => setParte(e.target.value as ParteResponsabile)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {PARTI.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </FormField>
      </div>
      <FormField label="Modulo (opzionale)">
        <select
          value={moduloId ?? ""}
          onChange={(e) => setModuloId(e.target.value || null)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="">— nessuno —</option>
          {moduli.map((m) => (
            <option key={m.id} value={m.id}>
              {m.codice} · {m.nome}
            </option>
          ))}
        </select>
      </FormField>
      <FormField label="Rivolta a (opzionale)">
        <select
          value={assegnatoId ?? ""}
          onChange={(e) => setAssegnatoId(e.target.value || null)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="">— tutti i destinatari Mister Pet —</option>
          {destinatariCandidati.map((u) => (
            <option key={u.email} value={u.utente_id!}>
              {u.nome_completo ?? u.email}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-muted-foreground mt-1">
          Se valorizzato, solo l'utente destinatario (più Maitime + ruoli
          Presidenza/Management) vede la voce.
        </p>
      </FormField>
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={visCliente}
          onChange={(e) => setVisCliente(e.target.checked)}
        />
        Visibile al cliente
      </label>

      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={mutation.isPending}>
          Annulla
        </Button>
        <Button size="sm" onClick={salva} disabled={mutation.isPending || !titolo.trim()}>
          {mutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
          Salva
        </Button>
      </div>
      {mutation.isError && (
        <p className="text-xs text-destructive">
          Errore: {(mutation.error as Error).message}
        </p>
      )}
    </div>
  );
}

// ── Form chiusura ────────────────────────────────────────────────────

function ChiudiForm({
  voce,
  onCancel,
  onClosed,
}: {
  voce: BachecaVoce;
  onCancel: () => void;
  onClosed: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const mutation = useChiudiBacheca();

  function chiudi() {
    if (!motivo.trim()) return;
    mutation.mutate(
      { voceId: voce.id, motivo_chiusura: motivo.trim() },
      { onSuccess: onClosed }
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">
        Chiudi voce
      </p>
      <div className="rounded-md border bg-amber-50/40 border-amber-200 px-3 py-2">
        <p className="text-xs text-amber-900 leading-relaxed">
          Stai chiudendo la voce. Il cliente non potrà più aggiungere messaggi.
          Indica il motivo (es. "risolto offline", "non più rilevante", "duplicato di voce X").
        </p>
      </div>
      <FormField label="Motivo chiusura">
        <Textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          autoFocus
        />
      </FormField>
      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={mutation.isPending}>
          Annulla
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={chiudi}
          disabled={mutation.isPending || !motivo.trim()}
        >
          {mutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
          Conferma chiusura
        </Button>
      </div>
      {mutation.isError && (
        <p className="text-xs text-destructive">
          Errore: {(mutation.error as Error).message}
        </p>
      )}
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
